import React, { useEffect, useState, useRef, useCallback } from 'react';
import ChatbotWidget from '../Platform/Chatbot/ChatbotWidget/ChatbotWidget';
import logger from '../../utils/logger';

function EmbedChatbot({ previewConfig = null, isPreview = false }) {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const widgetLoadedRef = useRef(false);

  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const urlChatbotId = urlParams.get('chatbotId');

  // Use previewConfig for hideToggleButton if available, otherwise check URL
  const hideToggleButton = (isPreview && previewConfig?.hideToggleButton !== undefined)
    ? previewConfig.hideToggleButton
    : urlParams.get('hideToggleButton') === 'true';

  const preload = urlParams.get('preload') === 'true';

  const chatbotId = isPreview && previewConfig?.chatbotId ? previewConfig.chatbotId : urlChatbotId;
  const BASE_URL = window.location.origin;

  // Initialize tracking scripts only on first user interaction (completely on-demand)
  const initializeTracking = useCallback(() => {
    if (isPreview || typeof window === 'undefined') return;

    logger.log('Initializing tracking scripts on user interaction...');

    // Initialize Microsoft Clarity only when user interacts (using proxy to avoid ad blockers)
    if (!window.clarity && !window.clarityInitializing) {
      window.clarityInitializing = true;

      // Define clarity function first
      window.clarity = window.clarity || function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };

      // Load Clarity directly from Microsoft on the Cloudflare-hosted frontend
      const clarityScript = document.createElement('script');
      clarityScript.type = 'text/javascript';
      clarityScript.async = true;
      clarityScript.src = 'https://www.clarity.ms/tag/s0qw88i4j9';
      clarityScript.onload = () => {
        logger.log('Microsoft Clarity initialized on user interaction');
      };
      clarityScript.onerror = () => {
        logger.warn('Failed to load Clarity script');
        window.clarityInitializing = false;
      };
      document.head.appendChild(clarityScript);
    }

  }, [isPreview]);

  // Handle messages from parent window (for DOM-based implementation)
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;

      // Handle chat opened message from parent
      if (event.data.type === 'CHAT_OPENED') {
        // Initialize tracking when chat is opened
        initializeTracking();

        // Store page context if provided (for knowledge generation)
        if (event.data.pageContext) {
          window.__smushchat_page_context = event.data.pageContext;
          logger.log('Received page context from parent:', event.data.pageContext.url);
        }

        // Set Clarity tag
        if (window.clarity) {
          window.clarity('set', 'chat_opened', chatbotId);
        }
      }

      // Handle chat closed message from parent
      if (event.data.type === 'CHAT_CLOSED') {
        // You can handle any cleanup when chat is closed
      }

      // Handle check ready message from parent
      if (event.data.type === 'CHECK_READY') {
        logger.log('Received CHECK_READY message from parent');

        // Store page context if provided
        if (event.data.pageContext) {
          window.__smushchat_page_context = event.data.pageContext;
          logger.log('Received page context from parent:', event.data.pageContext.url);
        }

        if (isFullyLoaded || widgetLoadedRef.current) {
          logger.log('Widget is ready, sending WIDGET_READY message');
          window.parent.postMessage({ type: 'WIDGET_READY' }, '*');
        }
      }

      // Handle page context message from parent
      if (event.data.type === 'PAGE_CONTEXT') {
        window.__smushchat_page_context = event.data.pageContext;
        logger.log('Received updated page context:', event.data.pageContext?.url);
      }

    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isFullyLoaded, isPreview, chatbotId, initializeTracking]);

  // When the component is fully loaded, notify the parent
  useEffect(() => {
    if (config && preload) {
      // Set a small delay to ensure rendering is complete
      const timer = setTimeout(() => {
        logger.log('Chatbot widget fully loaded, sending WIDGET_READY message');
        setIsFullyLoaded(true);
        widgetLoadedRef.current = true;
        window.parent.postMessage({ type: 'WIDGET_READY' }, '*');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [config, preload]);

  useEffect(() => {
    // If in preview mode and previewConfig is provided, use it directly
    if (isPreview && previewConfig) {
      setConfig(previewConfig);
      return;
    }

    // Otherwise, fetch config from API
    if (!chatbotId) {
      setError('No chatbotId provided in the URL.');
      return;
    }

    const fetchConfig = async () => {
      try {
        // Use the new secure public endpoint that only returns non-sensitive config
        const initialUrl = `${BASE_URL}/api/chatbot/public?chatbotId=${chatbotId}`;
        const response = await fetch(initialUrl);
        if (!response.ok) {
          throw new Error(`Error fetching configuration: ${response.statusText}`);
        }
        const data = await response.json();
        if (data?.config) {
          // Process the config to ensure it's valid and set it directly
          setConfig(data.config);
        } else {
          setError('Configuration not found for the provided chatbotId.');
        }
      } catch (error) {
        logger.error('Failed to fetch chatbot configuration:', error);
        setError('Failed to load chatbot configuration.');
      }
    };

    fetchConfig();
  }, [chatbotId, BASE_URL, isPreview, previewConfig]);

  const handleSaveConfig = async (newConfig) => {
    // In preview mode, we don't save to the server
    if (isPreview) {
      logger.log('Preview mode - config update:', newConfig);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/chatbot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatbotId,
          config: newConfig,
        }),
      });
      if (!response.ok) {
        throw new Error(`Error saving configuration: ${response.statusText}`);
      }
      logger.log('Configuration saved successfully.');
    } catch (error) {
      logger.error('Failed to save chatbot configuration:', error);
    }
  };


  const handleChatOpenStateChange = (isOpen) => {
    const message = isOpen
      ? { type: 'RESIZE_IFRAME', width: 400, height: 700 }
      : { type: 'RESIZE_IFRAME', width: 60, height: 60 };
    window.parent.postMessage(message, '*');

    // Initialize tracking only when user opens chat for the first time
    if (!isPreview && isOpen) {
      initializeTracking();

      // Set additional clarity tags after initialization
      setTimeout(() => {
        if (window.clarity) {
          window.clarity('set', 'chatbot_first_interaction', chatbotId);
          logger.log('Microsoft Clarity tracking activated - user opened chat');
        }
      }, 2000);
    }

    // Track in Clarity using custom tags (only if Clarity is loaded)
    setTimeout(() => {
      if (!isPreview && window.clarity) {
        window.clarity('set', isOpen ? 'chat_opened' : 'chat_closed', chatbotId);
      }
    }, 1500); // Small delay to ensure scripts are loaded
  };

  const handleClose = () => {
    // Send a message to the parent window to close the chat
    window.parent.postMessage({ type: 'CLOSE_CHAT' }, '*');
  };

  if (error && !isPreview) {
    return <div>Error: {error}</div>;
  }

  if (!config) {
    return <div></div>;
  }

  // Check master toggle (allow if it's a preview)
  if (!isPreview && config.widgetEnabled === false) {
    return null;
  }

  return (
    <ChatbotWidget
      {...config}
      isPlatform={isPreview}
      onSaveConfig={isPreview ? handleSaveConfig : undefined}
      onChatOpenStateChange={handleChatOpenStateChange}
      onClose={handleClose}
      hideToggleButton={hideToggleButton}
      initiallyOpen={hideToggleButton} // Open chat automatically when toggle is hidden
      toggleButtonSize={config?.toggleButtonSize || 'medium'}
      toggleButtonIcon={config?.toggleButtonIcon || ''}
      customToggleButtonIcon={config?.customToggleButtonIcon || ''}
      toggleButtonIconSize={config?.toggleButtonIconSize || '100'}
      toggleButtonBackgroundColor={config?.toggleButtonBackgroundColor || '#1a1a1a'}
      toggleButtonIconOffsetX={config?.toggleButtonIconOffsetX ?? 0}
      toggleButtonIconOffsetY={config?.toggleButtonIconOffsetY ?? 0}
      toggleButtonOutlineEnabled={config?.toggleButtonOutlineEnabled ?? false}
      toggleButtonOutlineColor={config?.toggleButtonOutlineColor || '#ffffff'}

      botIconOffsetX={config?.botIconOffsetX ?? 0}
      botIconOffsetY={config?.botIconOffsetY ?? 0}

      menuItems={config?.menuItems || []}
      forms={config?.forms || []}
      forceInputDisabled={config?.forceInputDisabled || false}
      soundNotificationEnabled={config?.soundNotificationEnabled !== false}
    />
  );
}

export default EmbedChatbot;
